// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { AsyncPipe, NgIf } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { map, startWith, tap } from 'rxjs/operators';
import { RouteService } from '../../services/routeService.service';
import { SystemOverviewService } from '../../services/systemOverview.service';
import { SystemDetailsComponent } from '../system-details/system-details.component';
import { ApplicationInsightsService } from '../../services/application-insights/application-insights-service/application-insights.service';

@Component({
  selector: 'app-system-root',
  standalone: true,
  templateUrl: './system-root.component.html',
  styleUrls: ['./system-root.component.scss'],
  imports: [
    NgIf,
    AsyncPipe,
    TranslateModule,
    RouterModule,
    SystemDetailsComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SystemRootComponent {
  notFound$ = this.systemOverviewService.systemDetails.error$.pipe(
    map((error) => error?.status === 404),
    startWith(false),
  );

  constructor(
    public readonly systemOverviewService: SystemOverviewService,
    protected routeService: RouteService,
    private route: ActivatedRoute,
    private readonly appInsightsService: ApplicationInsightsService,
  ) {
    // we ALWAYS trigger a load of the entire overview right away..
    this.systemOverviewService.systemDetails.lastSuccessfulData$
      .pipe(
        tap((systemDetails) => {
          this.systemOverviewService.entireOverview.request({
            systemSerialNumber: systemDetails.systemSerialNumber,
            systemMaterialNumber: systemDetails.systemMaterialNumber,
            modalityId: systemDetails.modalityId,
          });
          this.trackSystemOverviewOpenedEvent();
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    this.routeService.currentSystem$
      .pipe(takeUntilDestroyed())
      .subscribe((system) => {
        if (!system) {
          return;
        }
        const { systemSerialNumber, systemMaterialNumber } = system;

        this.systemOverviewService.clearStaleData();
        this.systemOverviewService.systemDetails.request({
          systemMaterialNumber,
          systemSerialNumber,
          modalityId: 0,
        });
      });
  }

  private trackSystemOverviewOpenedEvent(): void {
    this.appInsightsService.trackComponentUsageEvent('SystemOverviewPage');
  }
}
